import React, { useState, useEffect } from 'react';
import { PieChart, ChevronDown, ChevronRight, BarChart3 } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { Navigation } from '../components/Navigation';
import api from '../utils/api';

const formatNum = (val) => {
  if (val === null || val === undefined) return '';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '';
  return num.toLocaleString('da-DK', { maximumFractionDigits: 0 });
};

// Beslutter om en sektion semantisk er en udgift (omkostning).
// Bruges til at give Realiseret-celler korrekt farve: rød på udgifter, grøn på indtægter.
const isExpenseSection = (sectionType) => sectionType === 'expense' || sectionType === 'expense_total';
const isRevenueSection = (sectionType) => sectionType === 'revenue' || sectionType === 'db';

const CellValue = ({ value, type, bold, sectionType }) => {
  if (value === null || value === undefined || value === '' || value === 0) return <span className="text-white/10"></span>;
  const isNeg = value < 0;
  let color;
  if (type === 'result') {
    color = value >= 0 ? 'text-emerald-400' : 'text-rose-400';
  } else if (type === 'realiseret') {
    // Realiseret farve afhænger af om rækken er en udgift eller en indtægt:
    //  - Udgift realiseret = rødligt (penge ud)
    //  - Indtægt/DB realiseret = grøn hvis positivt, rødt hvis negativt
    if (isExpenseSection(sectionType)) {
      color = 'text-rose-300/85';
    } else if (isRevenueSection(sectionType)) {
      color = value >= 0 ? 'text-emerald-400/90' : 'text-rose-400/85';
    } else {
      color = isNeg ? 'text-rose-300/85' : 'text-emerald-400/90';
    }
  } else if (type === 'budget') {
    color = 'text-sky-400/80';
  } else if (type === 'revenue' || type === 'db') {
    color = 'text-[#1E6F7A]';
  } else if (type === 'expense_total') {
    color = 'text-rose-400/90';
  } else if (isNeg) {
    color = 'text-rose-400/70';
  } else {
    color = 'text-white/70';
  }
  return <span className={`${color} tabular-nums ${bold ? 'font-semibold' : ''}`}>{formatNum(value)}</span>;
};

const ChartTooltip = ({ active, payload, label, suffix }) => {
  if (!active || !payload?.length) return null;
  const unit = suffix || 'kr';
  return (
    <div className="bg-[#1a1e28] border border-white/10 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-white/50 text-[10px] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>
          {p.name}: {unit === '%' ? `${p.value}%` : `${formatNum(p.value)} kr`}
        </p>
      ))}
    </div>
  );
};

const DBCharts = ({ items, weekLabels }) => {
  const budgetItem = items.find(i => i.label === 'Samlet DB-Budget');
  const realItem = items.find(i => i.label === 'Samlet DB-Realiseret');
  const akkBudgetItem = items.find(i => (i.label || '').includes('DB-Budget Akk'));
  const akkRealItem = items.find(i => (i.label || '').includes('DB-Realiseret Akk'));

  const weeklyData = weekLabels.map(wk => ({
    name: `Uge ${wk}`,
    budget: budgetItem?.weeks?.[String(wk)] || 0,
    realiseret: realItem?.weeks?.[String(wk)] || 0,
  }));

  const akkData = weekLabels.map(wk => ({
    name: `Uge ${wk}`,
    budget: akkBudgetItem?.weeks?.[String(wk)] || 0,
    realiseret: akkRealItem?.weeks?.[String(wk)] || 0,
  }));

  const fmtY = (v) => `${(v / 1000).toFixed(0)}k`;

  return (
    <div className="px-5 py-4 border-t border-white/[0.04] bg-[#0d0f14]">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-3">DB — Budget vs. Realiseret</p>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" tickFormatter={fmtY} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }} />
                <Bar dataKey="budget" name="Budget" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="realiseret" name="Realiseret" fill="#10b981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-3">DB — Akkumuleret Budget vs. Realiseret</p>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={akkData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" tickFormatter={fmtY} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }} />
                <Line type="monotone" dataKey="budget" name="Budget Akk." stroke="#3b82f6" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2.5, fill: '#3b82f6' }} />
                <Line type="monotone" dataKey="realiseret" name="Realiseret Akk." stroke="#10b981" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2.5, fill: '#10b981' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

const OmsaetningCharts = ({ items, weekLabels }) => {
  const budgetItem = items.find(i => i.label === 'Omsætning-Budget');
  const realItem = items.find(i => i.label === 'Omsætning-Realiseret');
  const akkBudgetItem = items.find(i => (i.label || '').includes('Omsætning-Budget Akk'));
  const akkRealItem = items.find(i => (i.label || '').includes('Omsætning-Realiseret Akk'));

  const weeklyData = weekLabels.map(wk => ({
    name: `Uge ${wk}`,
    budget: budgetItem?.weeks?.[String(wk)] || 0,
    realiseret: realItem?.weeks?.[String(wk)] || 0,
  }));

  const akkData = weekLabels.map(wk => ({
    name: `Uge ${wk}`,
    budget: akkBudgetItem?.weeks?.[String(wk)] || 0,
    realiseret: akkRealItem?.weeks?.[String(wk)] || 0,
  }));

  const fmtY = (v) => `${(v / 1000).toFixed(0)}k`;

  return (
    <div className="px-5 py-4 border-t border-white/[0.04] bg-[#0d0f14]">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-3">Omsætning — Budget vs. Realiseret</p>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" tickFormatter={fmtY} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }} />
                <Bar dataKey="budget" name="Budget" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="realiseret" name="Realiseret" fill="#10b981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-3">Omsætning — Akkumuleret Budget vs. Realiseret</p>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={akkData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" tickFormatter={fmtY} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }} />
                <Line type="monotone" dataKey="budget" name="Budget Akk." stroke="#3b82f6" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2.5, fill: '#3b82f6' }} />
                <Line type="monotone" dataKey="realiseret" name="Realiseret Akk." stroke="#10b981" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2.5, fill: '#10b981' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};


const DPTChart = ({ items, weekLabels }) => {
  // Ekskluder indeværende uge (data ufuldstændig) — sammenligning skal være på lukkede uger
  const _curr = getCurrentIsoWeek();
  const _wkLabels = (weekLabels || []).filter(wk => wk < _curr);
  const budgetItem = items.find(i => i.label === 'DPT');
  const realItem = items.find(i => i.label === 'DPT-Realiseret');
  const dgBudgetItem = items.find(i => i.label === 'DG');
  const dgRealItem = items.find(i => i.label === 'DG-Realiseret');

  const dptData = _wkLabels.map(wk => ({
    name: `Uge ${wk}`,
    budget: budgetItem?.weeks?.[String(wk)] || 0,
    realiseret: realItem?.weeks?.[String(wk)] || null,
  })).filter(d => d.budget > 0 || d.realiseret > 0);

  const dgData = _wkLabels.map(wk => ({
    name: `Uge ${wk}`,
    budget: dgBudgetItem?.weeks?.[String(wk)] || 0,
    realiseret: dgRealItem?.weeks?.[String(wk)] || null,
  })).filter(d => d.budget > 0 || d.realiseret > 0);

  const dptRealValues = dptData.filter(d => d.realiseret && d.realiseret > 0).map(d => d.realiseret);
  const dptAvg = dptRealValues.length > 0 ? Math.round(dptRealValues.reduce((s, v) => s + v, 0) / dptRealValues.length) : 0;

  const dgRealValues = dgData.filter(d => d.realiseret && d.realiseret > 0).map(d => d.realiseret);
  const dgAvg = dgRealValues.length > 0 ? Math.round(dgRealValues.reduce((s, v) => s + v, 0) / dgRealValues.length * 10) / 10 : 0;

  return (
    <div className="px-5 py-4 border-t border-white/[0.04] bg-[#0d0f14]">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-3">DPT — Forventet vs. Realiseret</p>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dptData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" domain={['auto', 'auto']} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }} />
                <Line type="monotone" dataKey="budget" name="DPT Forventet" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2.5, fill: '#3b82f6' }} />
                <Line type="monotone" dataKey="realiseret" name="DPT Realiseret" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }} connectNulls={false} />
                {dptAvg > 0 && <ReferenceLine y={dptAvg} stroke="#8B5CF6" strokeDasharray="6 3" label={{ value: `Gns: ${dptAvg}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-3">DG — Forventet vs. Realiseret</p>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dgData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
                <Tooltip content={<ChartTooltip suffix="%" />} />
                <Legend wrapperStyle={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }} />
                <Line type="monotone" dataKey="budget" name="DG Forventet" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2.5, fill: '#3b82f6' }} />
                <Line type="monotone" dataKey="realiseret" name="DG Realiseret" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }} connectNulls={false} />
                {dgAvg > 0 && <ReferenceLine y={dgAvg} stroke="#8B5CF6" strokeDasharray="6 3" label={{ value: `Gns: ${dgAvg}%`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

// === KPI Summary Strip — viser periodens samlede tal som hurtig statusoversigt ===
// Top-niveau sektioner der kan foldes sammen — Lønninger fungerer som paraply for sine subsektioner
const COLLAPSIBLE_GROUPS = ['Beregningsgrundlag', 'Omsætning', 'Dækningsbidrag', 'Lønninger', 'Autoudgifter', 'Salgsomkostninger', 'Øvrige udgifter'];

// Grupper sektioner: hver gang vi rammer en gruppe-titel, starter en ny gruppe.
// Sektioner mellem to gruppe-titler tilhører den foregående gruppe (fx Fast løn - Ledelse under Lønninger).
// "Sum" og andre result-sektioner bliver ikke en del af en gruppe.
const groupSections = (sections) => {
  if (!sections) return [];
  const groups = [];
  let current = null;
  for (const sec of sections) {
    if (COLLAPSIBLE_GROUPS.includes(sec.title)) {
      if (current) groups.push(current);
      current = { title: sec.title, sections: [sec], isGroup: true };
    } else if (current && sec.type !== 'result') {
      current.sections.push(sec);
    } else {
      if (current) { groups.push(current); current = null; }
      groups.push({ title: sec.title, sections: [sec], isGroup: false });
    }
  }
  if (current) groups.push(current);
  return groups;
};
// Beregner indeværende ISO-uge (mandag-baseret) — bruges til at begrænse "Budget til dato"
const getCurrentIsoWeek = () => {
  const now = new Date();
  const target = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil((((target - yearStart) / 86400000) + 1) / 7);
};

const KpiCard = ({ label, budget, realiseret, isPositive, accentColor, testId, ytdWeekCount, totalWeekCount, weekRange }) => {
  const hasReal = realiseret !== null && realiseret !== undefined;
  const variance = hasReal && budget ? realiseret - budget : null;
  const variancePct = hasReal && budget ? (variance / Math.abs(budget)) * 100 : null;
  // For udgifter: positiv afvigelse = overforbrug (dårligt). For indtægter/DB: positiv afvigelse = bedre end budget (godt).
  const isGood = variance === null ? null : (isPositive ? variance >= 0 : variance <= 0);
  const arrow = variance === null ? '—' : variance > 0 ? '↑' : variance < 0 ? '↓' : '→';
  return (
    <div data-testid={testId} className="bg-[#13151a] border border-white/[0.06] rounded-xl px-5 py-4 hover:border-white/[0.12] transition-colors">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-[0.15em] text-white/35 font-semibold">{label}</span>
        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accentColor }} />
      </div>
      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] uppercase tracking-wider text-white/30">
            Budget {weekRange ? <span className="opacity-60 normal-case tracking-normal">til dato ({weekRange})</span> : ''}
          </span>
          <span className="text-sky-400/80 text-sm font-semibold tabular-nums">{formatNum(budget)}</span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] uppercase tracking-wider text-white/30">Realiseret</span>
          <span className={`text-sm font-semibold tabular-nums ${
            !hasReal ? 'text-white/20' :
            isPositive ? (realiseret >= 0 ? 'text-emerald-400/90' : 'text-rose-400/85')
              : 'text-rose-300/85'
          }`}>{hasReal ? formatNum(realiseret) : '—'}</span>
        </div>
        {variance !== null && (
          <div className="pt-1.5 mt-1.5 border-t border-white/[0.05] flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-wider text-white/30">Afvigelse</span>
            <span className={`text-xs font-semibold tabular-nums ${
              isGood ? 'text-emerald-400/90' : 'text-rose-400/85'
            }`}>
              {arrow} {formatNum(Math.abs(variance))}
              {variancePct !== null && Math.abs(variancePct) < 999 && (
                <span className="ml-1.5 text-[10px] opacity-70">({variancePct >= 0 ? '+' : ''}{variancePct.toFixed(1)}%)</span>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Aggregerer KPI-tal fra sections til den valgte periode.
// Vigtigt: Budget summeres KUN op til og med indeværende ISO-uge — så den er
// sammenlignelig med Realiseret (som naturligt kun har data til dato).
const computeKpiSummary = (sections, weekLabels, currentIsoWeek) => {
  if (!sections || !weekLabels?.length) return null;
  // Begræns budgettet til uger STRENGT FØR indeværende ISO-uge (eksl. denne uge)
  // — så indeværende ufærdige uge ikke skaber falsk afvigelse.
  const ytdWeeks = (weekLabels || []).filter(wk => !currentIsoWeek || wk < currentIsoWeek).map(String);
  const allWeeks = weekLabels.map(String);
  const sumYtd = (item) => ytdWeeks.reduce((s, w) => s + (item?.weeks?.[w] || 0), 0);
  const sumAll = (item) => allWeeks.reduce((s, w) => s + (item?.weeks?.[w] || 0), 0);
  const findItem = (sectionTitle, labelMatch) => {
    const sec = sections.find(s => s.title === sectionTitle);
    if (!sec) return null;
    return sec.items?.find(i => labelMatch(i.label || ''));
  };

  // Omsætning
  const omsBudget = findItem('Omsætning', l => l === 'Omsætning-Budget');
  const omsReal = findItem('Omsætning', l => l === 'Omsætning-Realiseret');
  // DB
  const dbBudget = findItem('Dækningsbidrag', l => l === 'Samlet DB-Budget');
  const dbReal = findItem('Dækningsbidrag', l => l === 'Samlet DB-Realiseret');

  // Udgifter total: Løn + Auto + Salgs + Øvrige (budget) og deres realiseret-modparter
  const collect = (labels, summer) =>
    labels.reduce((acc, lbl) => {
      for (const sec of sections) {
        const it = sec.items?.find(i => i.label === lbl);
        if (it) acc += summer(it);
      }
      return acc;
    }, 0);
  const expenseBudgetTotal = collect(
    ['Løn i alt', 'Autoudgifter i alt', 'Annoncering', 'Værktøj og berner'],
    sumYtd,
  );
  const expenseRealTotal = collect(
    ['Løn i alt - Realiseret', 'Autoudgifter - Realiseret i alt', 'Annoncering - Realiseret', 'Værktøj og berner - Realiseret'],
    sumYtd,
  );

  return {
    ytdWeekCount: ytdWeeks.length,
    totalWeekCount: allWeeks.length,
    omsaetning: { budget: omsBudget ? sumYtd(omsBudget) : 0, realiseret: omsReal ? sumYtd(omsReal) : null },
    db: { budget: dbBudget ? sumYtd(dbBudget) : 0, realiseret: dbReal ? sumYtd(dbReal) : null },
    udgifter: { budget: expenseBudgetTotal, realiseret: expenseRealTotal !== 0 ? expenseRealTotal : null },
  };
};

// === Generisk Budget vs Realiseret graf-komponent ===
// Bruges af alle sektioner der har par af Budget/Realiseret rækker (Løn, Auto, Salgs, Øvrige, Sum)
// Renderer 2 grafer side om side: ugentlig (BarChart) + akkumuleret (LineChart)
const GenericBudgetChart = ({ items, weekLabels, config }) => {
  const budgetItem = items.find(i => i.label === config.budgetLabel);
  const realItem = items.find(i => i.label === config.realLabel);
  const akkBudgetItem = items.find(i => i.label === config.akkBudgetLabel);
  const akkRealItem = items.find(i => i.label === config.akkRealLabel);

  const sign = config.invert ? -1 : 1;

  const weeklyData = weekLabels.map(wk => ({
    name: `Uge ${wk}`,
    budget: (budgetItem?.weeks?.[String(wk)] || 0) * sign,
    realiseret: (realItem?.weeks?.[String(wk)] || 0) * sign,
  }));
  const akkData = weekLabels.map(wk => ({
    name: `Uge ${wk}`,
    budget: (akkBudgetItem?.weeks?.[String(wk)] || 0) * sign,
    realiseret: (akkRealItem?.weeks?.[String(wk)] || 0) * sign,
  }));

  const fmtY = (v) => `${(v / 1000).toFixed(0)}k`;
  const realColor = config.invert ? '#f43f5e' : '#10b981';

  return (
    <div className="px-5 py-4 border-t border-white/[0.04] bg-[#0d0f14]">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-3">{config.weeklyTitle}</p>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" tickFormatter={fmtY} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }} />
                <Bar dataKey="budget" name="Budget" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="realiseret" name="Realiseret" fill={realColor} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold mb-3">{config.akkTitle}</p>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={akkData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} stroke="rgba(255,255,255,0.06)" tickFormatter={fmtY} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }} />
                <Line type="monotone" dataKey="budget" name="Budget Akk." stroke="#3b82f6" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2.5, fill: '#3b82f6' }} />
                <Line type="monotone" dataKey="realiseret" name="Realiseret Akk." stroke={realColor} strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2.5, fill: realColor }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

// Chart-konfigurationer pr. sektion-titel — peger på de rette item-labels i hver sektion.
// `sourceSection` (valgfri) bruges når chart-toggle vises på en header-sektion uden egne items
// — så finder vi items fra den navngivne søster-sektion i stedet.
const SECTION_CHART_CONFIG = {
  'Lønninger': {
    sourceSection: 'Løn - Totaler',
    budgetLabel: 'Løn i alt',
    realLabel: 'Løn i alt - Realiseret',
    akkBudgetLabel: 'Løn i alt Akk.',
    akkRealLabel: 'Løn i alt - Realiseret Akk.',
    invert: true,
    weeklyTitle: 'Lønninger — Budget vs. Realiseret',
    akkTitle: 'Lønninger — Akkumuleret Budget vs. Realiseret',
  },
  'Autoudgifter': {
    budgetLabel: 'Autoudgifter i alt',
    realLabel: 'Autoudgifter - Realiseret i alt',
    akkBudgetLabel: 'Autoudgifter i alt Akk.',
    akkRealLabel: 'Autoudgifter - Realiseret i alt Akk.',
    invert: true,
    weeklyTitle: 'Autoudgifter — Budget vs. Realiseret',
    akkTitle: 'Autoudgifter — Akkumuleret Budget vs. Realiseret',
  },
  'Salgsomkostninger': {
    budgetLabel: 'Annoncering',
    realLabel: 'Annoncering - Realiseret',
    akkBudgetLabel: 'Annoncering Akk.',
    akkRealLabel: 'Annoncering - Realiseret Akk.',
    invert: true,
    weeklyTitle: 'Salgsomkostninger — Budget vs. Realiseret',
    akkTitle: 'Salgsomkostninger — Akkumuleret Budget vs. Realiseret',
  },
  'Øvrige udgifter': {
    budgetLabel: 'Værktøj og berner',
    realLabel: 'Værktøj og berner - Realiseret',
    akkBudgetLabel: 'Værktøj og berner Akk.',
    akkRealLabel: 'Værktøj og berner - Realiseret Akk.',
    invert: true,
    weeklyTitle: 'Øvrige udgifter — Budget vs. Realiseret',
    akkTitle: 'Øvrige udgifter — Akkumuleret Budget vs. Realiseret',
  },
  'Sum': {
    budgetLabel: 'Sum Budget',
    realLabel: 'Sum Realiseret',
    akkBudgetLabel: 'Sum Budget Akk.',
    akkRealLabel: 'Sum Realiseret Akk.',
    invert: false,
    weeklyTitle: 'Bundlinje — Budget vs. Realiseret',
    akkTitle: 'Bundlinje — Akkumuleret Budget vs. Realiseret',
  },
};

const BudgetSection = ({ section, weekLabels, isLast, allSections, hideTitle = false, highlightedWeek = null }) => {
  const [expanded, setExpanded] = useState(!section.collapsible);
  const [showChart, setShowChart] = useState(false);
  const isTotal = section.type === 'expense_total' || section.type === 'result';
  const isHeader = section.type === 'header';
  const isResult = section.type === 'result';
  const isDB = section.title === 'Dækningsbidrag';
  const isBasis = section.title === 'Beregningsgrundlag';
  const isOmsaetning = section.title === 'Omsætning';
  // Generisk chart-support: hvis sektion-titlen findes i SECTION_CHART_CONFIG, kan vi vise toggle + chart
  const genericChartConfig = SECTION_CHART_CONFIG[section.title] || null;
  const hasGenericChart = !!genericChartConfig;
  // Hvis chart-config peger på en søster-sektion (header-tilfælde), find dens items
  const chartItems = (() => {
    if (!genericChartConfig) return section.items || [];
    if (genericChartConfig.sourceSection && allSections) {
      const src = allSections.find(s => s.title === genericChartConfig.sourceSection);
      if (src) return src.items || [];
    }
    return section.items || [];
  })();

  const sectionSummary = section.collapsible ? (() => {
    const sums = {};
    (section.items || []).forEach(item => {
      // Realiseret-rækker skal ikke tælles med i Budget-summen for sektionen
      if ((item.label || '').toLowerCase().includes('realiseret')) return;
      weekLabels.forEach(wk => {
        const k = String(wk);
        sums[k] = (sums[k] || 0) + (item.weeks?.[k] || 0);
      });
    });
    return sums;
  })() : null;

  if (isHeader) {
    if (hideTitle) {
      // Titlen vises på den ydre group-toggle-bar — vis kun evt. chart-toggle her
      return hasGenericChart ? (
        <div className="border-t border-white/[0.06] bg-[#0f1014]">
          <div className="px-5 py-1.5 flex items-center gap-2">
            <button
              data-testid={`toggle-chart-${(section.title || '').replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => setShowChart(!showChart)}
              className={`p-1 rounded transition-colors ${showChart ? 'bg-[#1E6F7A]/20 text-[#1E6F7A]' : 'text-white/20 hover:text-white/50 hover:bg-white/5'}`}
            >
              <BarChart3 className="w-3.5 h-3.5" />
            </button>
            <span className="text-[10px] text-white/30">Graf</span>
          </div>
          {showChart && <GenericBudgetChart items={chartItems} weekLabels={weekLabels} config={genericChartConfig} />}
        </div>
      ) : null;
    }
    return (
      <div className="border-t border-white/[0.06] bg-[#0f1014]">
        <div className="px-5 py-2 flex items-center gap-2">
          {hasGenericChart && (
            <button
              data-testid={`toggle-chart-${(section.title || '').replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => setShowChart(!showChart)}
              className={`p-1 rounded transition-colors ${showChart ? 'bg-[#1E6F7A]/20 text-[#1E6F7A]' : 'text-white/20 hover:text-white/50 hover:bg-white/5'}`}
            >
              <BarChart3 className="w-3.5 h-3.5" />
            </button>
          )}
          <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/30">{section.title}</span>
        </div>
        {hasGenericChart && showChart && (
          <GenericBudgetChart items={chartItems} weekLabels={weekLabels} config={genericChartConfig} />
        )}
      </div>
    );
  }

  return (
    <div className={`
      ${isResult ? 'bg-gradient-to-r from-[#1E6F7A]/15 via-[#1E6F7A]/5 to-transparent border-t-2 border-[#1E6F7A]/50 mt-2 shadow-[inset_0_1px_0_rgba(30,111,122,0.15)]' : ''}
      ${isTotal && !isResult ? 'bg-white/[0.025] border-t border-white/[0.08]' : ''}
      ${section.highlight && !isResult ? 'bg-[#1E6F7A]/[0.04]' : ''}
    `}>
      {section.collapsible ? (
        <div
          onClick={() => setExpanded(!expanded)}
          className="grid items-center text-xs cursor-pointer transition-colors hover:bg-white/[0.03] border-t border-white/[0.04]"
          style={{ gridTemplateColumns: `220px 40px repeat(${weekLabels.length}, 1fr) 100px` }}
        >
          <div className="px-5 py-2 flex items-center gap-2">
            <span className={`transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'}`}>
              <ChevronDown className="w-3.5 h-3.5 text-white/30" />
            </span>
            <span className="text-xs font-medium text-white/60">{section.title}</span>
          </div>
          <div></div>
          {weekLabels.map(wk => (
            <div key={wk} className={`text-right px-2 py-2 ${highlightedWeek === wk ? 'bg-[#1E6F7A]/15' : ''}`}>
              <CellValue value={sectionSummary[String(wk)]} type={section.type} sectionType={section.type} bold />
            </div>
          ))}
          <div className="text-right px-5 py-2">
            <CellValue value={weekLabels.reduce((s, wk) => s + (sectionSummary[String(wk)] || 0), 0)} type={section.type} sectionType={section.type} bold />
          </div>
        </div>
      ) : (
        section.items?.length > 0 && !isTotal && !isResult && !hideTitle && (
          <div className="border-t border-white/[0.04] px-5 py-2 flex items-center gap-2">
            {(isDB || isBasis || isOmsaetning || hasGenericChart) && (
              <button
                data-testid={`toggle-chart-${(section.title || '').replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => setShowChart(!showChart)}
                className={`p-1 rounded transition-colors ${showChart ? 'bg-[#1E6F7A]/20 text-[#1E6F7A]' : 'text-white/20 hover:text-white/50 hover:bg-white/5'}`}
              >
                <BarChart3 className="w-3.5 h-3.5" />
              </button>
            )}
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/30">{section.title}</span>
          </div>
        )
      )}
      {/* Chart-knap når titlen er skjult (sektion er første i en gruppe) */}
      {!section.collapsible && section.items?.length > 0 && !isTotal && !isResult && hideTitle && (isDB || isBasis || isOmsaetning || hasGenericChart) && (
        <div className="border-t border-white/[0.04] px-5 py-1.5 flex items-center gap-2">
          <button
            data-testid={`toggle-chart-${(section.title || '').replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => setShowChart(!showChart)}
            className={`p-1 rounded transition-colors ${showChart ? 'bg-[#1E6F7A]/20 text-[#1E6F7A]' : 'text-white/20 hover:text-white/50 hover:bg-white/5'}`}
          >
            <BarChart3 className="w-3.5 h-3.5" />
          </button>
          <span className="text-[10px] text-white/30">Graf</span>
        </div>
      )}

      {isTotal && !section.collapsible && !isResult && !['Fast løn - Ialt', 'Løn - Totaler', 'Freelancer - Realiseret'].includes(section.title) && (
        <div className="px-5 py-1.5 flex items-center gap-2">
          {hasGenericChart && (
            <button
              data-testid={`toggle-chart-${(section.title || '').replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => setShowChart(!showChart)}
              className={`p-1 rounded transition-colors ${showChart ? 'bg-[#1E6F7A]/20 text-[#1E6F7A]' : 'text-white/20 hover:text-white/50 hover:bg-white/5'}`}
            >
              <BarChart3 className="w-3.5 h-3.5" />
            </button>
          )}
          <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-white/25">{section.title}</span>
        </div>
      )}
      {/* Chart-knap uden titel for skjulte totaler (Fast løn - Ialt, Løn - Totaler) */}
      {isTotal && !section.collapsible && !isResult && ['Fast løn - Ialt', 'Løn - Totaler', 'Freelancer - Realiseret'].includes(section.title) && hasGenericChart && (
        <div className="px-5 py-1.5 flex items-center gap-2">
          <button
            data-testid={`toggle-chart-${(section.title || '').replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => setShowChart(!showChart)}
            className={`p-1 rounded transition-colors ${showChart ? 'bg-[#1E6F7A]/20 text-[#1E6F7A]' : 'text-white/20 hover:text-white/50 hover:bg-white/5'}`}
          >
            <BarChart3 className="w-3.5 h-3.5" />
          </button>
          <span className="text-[10px] text-white/30">Graf</span>
        </div>
      )}

      {isResult && (
        <div className="px-5 py-2 flex items-center gap-2">
          <div className="w-1 h-3.5 bg-[#1E6F7A] rounded-full" />
          <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#1E6F7A]">Bundlinje — {section.title}</span>
          {hasGenericChart && (
            <button
              data-testid={`toggle-chart-${(section.title || '').replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => setShowChart(!showChart)}
              className={`ml-auto p-1 rounded transition-colors ${showChart ? 'bg-[#1E6F7A]/20 text-[#1E6F7A]' : 'text-white/30 hover:text-white/60 hover:bg-white/5'}`}
            >
              <BarChart3 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {(expanded || !section.collapsible) && section.items?.map((item, idx) => {
        const label = item.label || '';
        const isSubTotal = label.toLowerCase().includes('ialt') || label.toLowerCase().includes('i alt');
        const isRealiseret = label.includes('Realiseret');
        const isBudgetRow = label.includes('Budget');
        const isResultRow = isResult;
        const isLoenIalt = label === 'Løn i alt';
        const isAkkRow = /\bAkk\.?\b/i.test(label);

        const rowType = isRealiseret ? 'realiseret'
          : isBudgetRow ? 'budget'
          : isResultRow ? 'result'
          : section.type;

        // Subtile kant- og baggrundsfarver der respekterer udgift/indtægt-semantik
        const isExpense = isExpenseSection(section.type);
        const isRevenue = isRevenueSection(section.type);
        const realisertBgClass = isRealiseret
          ? (isExpense ? 'bg-rose-500/[0.025] border-l-2 border-l-rose-500/25'
             : isRevenue ? 'bg-emerald-500/[0.04] border-l-2 border-l-emerald-500/30'
             : 'bg-emerald-500/[0.03] border-l-2 border-l-emerald-500/25')
          : '';
        const budgetBgClass = isBudgetRow ? 'bg-sky-500/[0.025] border-l-2 border-l-sky-500/20' : '';

        return (
          <div
            key={idx}
            data-testid={`budget-row-${(label || '').replace(/\s+/g, '-').toLowerCase()}`}
            className={`grid items-center text-xs transition-colors
              ${realisertBgClass}
              ${budgetBgClass}
              ${isSubTotal || isLoenIalt ? 'bg-white/[0.03]' : ''}
              ${isAkkRow ? 'opacity-90' : ''}
              ${isResultRow ? '' : 'border-t border-white/[0.03]'}
              ${isResultRow ? 'border-t border-[#1E6F7A]/10' : ''}
              hover:bg-white/[0.025]
            `}
            style={{ gridTemplateColumns: `220px 40px repeat(${weekLabels.length}, 1fr) 100px` }}
          >
            <div
              className={`px-5 py-1.5 truncate ${
                isRealiseret ? (isExpense ? 'text-rose-300/70 pl-7' : 'text-emerald-400/80 pl-7') :
                isBudgetRow ? 'text-sky-400/70 pl-7' :
                isSubTotal || isLoenIalt ? 'text-white/65 font-medium' :
                isResultRow ? 'text-white font-semibold' :
                section.collapsible ? 'text-white/40 pl-10' :
                'text-white/55'
              }`}
              title={label}
            >
              {label}
            </div>
            <div className="text-white/20 text-center text-[10px]">{item.extra}</div>
            {weekLabels.map(wk => (
              <div key={wk} className={`text-right px-2 py-1.5 ${highlightedWeek === wk ? 'bg-[#1E6F7A]/15' : ''}`}>
                <CellValue value={item.weeks?.[String(wk)]} type={rowType} sectionType={section.type} bold={isSubTotal || isLoenIalt || isResultRow} />
              </div>
            ))}
            <div className="text-right px-5 py-1.5">
              <CellValue
                value={
                  // Akk-rækker er allerede løbende totaler — "I alt" skal vise sidste uges værdi (= periodens total),
                  // IKKE en sum af alle ugerne (det ville dobbelttælle).
                  /\bAkk\.?\b/i.test(label)
                    ? (() => {
                        for (let i = weekLabels.length - 1; i >= 0; i--) {
                          const v = item.weeks?.[String(weekLabels[i])];
                          if (v !== undefined && v !== null && v !== 0) return v;
                        }
                        return item.weeks?.[String(weekLabels[weekLabels.length - 1])] || 0;
                      })()
                    : (weekLabels.reduce((s, wk) => s + (item.weeks?.[String(wk)] || 0), 0) || item.total)
                }
                type={rowType}
                sectionType={section.type}
                bold
              />
            </div>
          </div>
        );
      })}

      {isDB && showChart && <DBCharts items={section.items || []} weekLabels={weekLabels} />}
      {isBasis && showChart && <DPTChart items={section.items || []} weekLabels={weekLabels} />}
      {isOmsaetning && showChart && <OmsaetningCharts items={section.items || []} weekLabels={weekLabels} />}
      {hasGenericChart && showChart && !isDB && !isBasis && !isOmsaetning && (
        <GenericBudgetChart items={section.items || []} weekLabels={weekLabels} config={genericChartConfig} />
      )}
    </div>
  );
};

export default function Budget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    const m = new Date().getMonth();
    return m < 3 ? 1 : m < 6 ? 2 : m < 9 ? 3 : 4;
  });
  // Foldede top-niveau-sektioner — som udgangspunkt er ALLE lukkede
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set(COLLAPSIBLE_GROUPS));
  // Fremhævet uge-kolonne (klik på uge-nummer i header)
  const [highlightedWeek, setHighlightedWeek] = useState(null);
  const toggleGroup = (title) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title); else next.add(title);
      return next;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/budget/koerende-salg');
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Kunne ikke hente budget-data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getWeeksForQuarter = (q) => {
    const weeks = [];
    for (let wn = 1; wn <= 52; wn++) {
      const jan4 = new Date(2026, 0, 4);
      const dow = jan4.getDay() || 7;
      const monday = new Date(jan4);
      monday.setDate(jan4.getDate() - (dow - 1) + (wn - 1) * 7);
      const month = monday.getMonth();
      const quarter = month < 3 ? 1 : month < 6 ? 2 : month < 9 ? 3 : 4;
      if (quarter === q) weeks.push(wn);
    }
    return weeks;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1E6F7A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0C10] p-6">
        <div className="max-w-lg mx-auto bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-red-400 text-center text-sm">{error}</div>
      </div>
    );
  }

  const filteredWeeks = getWeeksForQuarter(selectedQuarter);
  const quarterLabels = { 1: 'Januar – Marts', 2: 'April – Juni', 3: 'Juli – September', 4: 'Oktober – December' };
  const currentIsoWeek = getCurrentIsoWeek();
  const kpiSummary = computeKpiSummary(data?.sections, filteredWeeks, currentIsoWeek);
  // Beregn uge-range visning til "Budget til dato (uge X – Y)" — eksl. indeværende uge
  const ytdWeeksInQ = filteredWeeks.filter(wk => wk < currentIsoWeek);
  const ytdRangeText = ytdWeeksInQ.length === 0
    ? null
    : ytdWeeksInQ.length === 1
      ? `uge ${ytdWeeksInQ[0]}`
      : `uge ${ytdWeeksInQ[0]}–${ytdWeeksInQ[ytdWeeksInQ.length - 1]}`;

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="budget-page">
      <header className="bg-[#111318] border-b border-white/[0.06] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Navigation />
            <div>
              <h1 className="text-base font-semibold text-white flex items-center gap-2.5">
                <PieChart className="w-4 h-4 text-[#1E6F7A]" />
                Budget — Kørende salg
              </h1>
              <p className="text-white/30 text-[11px] mt-0.5 ml-[26px]">{quarterLabels[selectedQuarter]} 2026 · {filteredWeeks.length} uger</p>
            </div>
          </div>
          <div className="flex items-center gap-0.5 bg-white/[0.04] rounded-lg p-0.5" data-testid="quarter-selector">
            {[1, 2, 3, 4].map(q => (
              <button
                key={q}
                data-testid={`quarter-${q}`}
                onClick={() => setSelectedQuarter(q)}
                className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                  selectedQuarter === q
                    ? 'bg-[#1E6F7A] text-white shadow-lg shadow-[#1E6F7A]/20'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/[0.04]'
                }`}
              >
                Q{q}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="p-3 md:p-5">
        {/* KPI Summary Strip — periodens samlede tal til dato */}
        {kpiSummary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="budget-kpi-strip">
            <KpiCard
              testId="kpi-omsaetning"
              label="Omsætning"
              budget={kpiSummary.omsaetning.budget}
              realiseret={kpiSummary.omsaetning.realiseret}
              isPositive={true}
              accentColor="#1E6F7A"
              weekRange={ytdRangeText}
            />
            <KpiCard
              testId="kpi-db"
              label="Dækningsbidrag"
              budget={kpiSummary.db.budget}
              realiseret={kpiSummary.db.realiseret}
              isPositive={true}
              accentColor="#3b82f6"
              weekRange={ytdRangeText}
            />
            <KpiCard
              testId="kpi-udgifter"
              label="Driftsudgifter"
              budget={kpiSummary.udgifter.budget}
              realiseret={kpiSummary.udgifter.realiseret}
              isPositive={false}
              accentColor="#f43f5e"
              weekRange={ytdRangeText}
            />
          </div>
        )}

        <div
          className="bg-[#13151a] rounded-xl border border-white/[0.05] overflow-auto shadow-2xl shadow-black/30"
          style={{ maxHeight: 'calc(100vh - 240px)' }}
        >
          {/* Sticky header */}
          <div
            className="grid items-center text-[10px] font-semibold uppercase tracking-wider text-white/25 border-b border-white/[0.06] bg-[#0e1015] sticky top-0 z-20"
            style={{ gridTemplateColumns: `220px 40px repeat(${filteredWeeks.length}, 1fr) 100px`, minWidth: `${260 + filteredWeeks.length * 90 + 100}px` }}
          >
            <div className="px-5 py-2.5">Post</div>
            <div></div>
            {filteredWeeks.map(wk => {
              const isHl = highlightedWeek === wk;
              return (
                <button
                  key={wk}
                  data-testid={`week-header-${wk}`}
                  onClick={() => setHighlightedWeek(isHl ? null : wk)}
                  className={`text-right px-2 py-2.5 transition-colors cursor-pointer ${
                    isHl
                      ? 'bg-[#1E6F7A]/25 text-[#5fbac4] ring-1 ring-[#1E6F7A]/50'
                      : 'hover:bg-white/[0.04] hover:text-white/60'
                  }`}
                  title={isHl ? 'Klik for at fjerne fremhævning' : 'Klik for at fremhæve denne uge'}
                >
                  Uge {wk}
                </button>
              );
            })}
            <div className="text-right px-5 py-2.5 text-white/40">I alt</div>
          </div>

          {/* Sections — grupperet under collapsible top-niveau sektioner */}
          <div style={{ minWidth: `${260 + filteredWeeks.length * 90 + 100}px` }}>
            {groupSections(data?.sections).map((group, gIdx) => {
              const isCollapsed = collapsedGroups.has(group.title);
              if (!group.isGroup) {
                // Standalone (fx Sum) — vis altid
                return group.sections.map((section, idx) => (
                  <BudgetSection
                    key={`${gIdx}-${idx}`}
                    section={section}
                    weekLabels={filteredWeeks}
                    isLast={gIdx === groupSections(data?.sections).length - 1 && idx === group.sections.length - 1}
                    allSections={data.sections}
                    highlightedWeek={highlightedWeek}
                  />
                ));
              }
              return (
                <div key={gIdx} data-testid={`group-${group.title.replace(/\s+/g, '-').toLowerCase()}`}>
                  {/* Collapse-toggle øverst i gruppen */}
                  <button
                    onClick={() => toggleGroup(group.title)}
                    data-testid={`toggle-group-${group.title.replace(/\s+/g, '-').toLowerCase()}`}
                    className="w-full grid items-center text-xs cursor-pointer transition-colors hover:bg-white/[0.04] bg-[#10131a] border-t border-white/[0.08] text-left"
                    style={{ gridTemplateColumns: `220px 40px repeat(${filteredWeeks.length}, 1fr) 100px` }}
                  >
                    <div className="px-5 py-2.5 flex items-center gap-2">
                      <span className={`transition-transform duration-200 inline-block ${isCollapsed ? '-rotate-90' : 'rotate-0'}`}>
                        <ChevronDown className="w-3.5 h-3.5 text-white/40" />
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/55">{group.title}</span>
                      {isCollapsed && <span className="text-[10px] text-white/25 ml-1">(klik for at åbne)</span>}
                    </div>
                  </button>
                  {!isCollapsed && group.sections.map((section, idx) => (
                    <BudgetSection
                      key={`${gIdx}-${idx}`}
                      section={section}
                      weekLabels={filteredWeeks}
                      isLast={false}
                      allSections={data.sections}
                      hideTitle={idx === 0 && section.title === group.title}
                      highlightedWeek={highlightedWeek}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
